import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  MdAdd, MdClose, MdTrendingUp, MdAttachMoney,
  MdCalendarToday, MdPerson, MdCheck, MdCancel
} from 'react-icons/md';
import api from '../services/api';
import { useModal } from '../context/ModalContext';

/* ─── LAYOUT ────────────────────────────────────────────────────── */

const PageContainer = styled.div`
  width: 100%;
  max-width: 100%;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${p => p.theme.spacing.xl};
  gap: ${p => p.theme.spacing.md};
  flex-wrap: wrap;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${p => p.theme.spacing.lg};
  flex-wrap: wrap;
`;

const Title = styled.h1`
  font-size: ${p => p.theme.fontSizes.xxxl};
  color: ${p => p.theme.colors.textPrimary};
  margin: 0;
`;

const PipelineValue = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
  font-size: ${p => p.theme.fontSizes.md};
  color: ${p => p.theme.colors.textSecondary};
  background: ${p => p.theme.colors.backgroundLight};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  padding: ${p => p.theme.spacing.sm} ${p => p.theme.spacing.lg};

  strong {
    color: ${p => p.theme.colors.success};
    font-size: ${p => p.theme.fontSizes.lg};
  }

  svg { color: ${p => p.theme.colors.success}; }
`;

const AddButton = styled.button`
  padding: 10px ${p => p.theme.spacing.lg};
  background: linear-gradient(135deg, ${p => p.theme.colors.primary}, ${p => p.theme.colors.primaryDark});
  color: white;
  border: none;
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: 600;
  font-family: ${p => p.theme.fonts.primary};
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
  cursor: pointer;
  transition: ${p => p.theme.transitions.fast};
  box-shadow: 0 4px 14px ${p => p.theme.colors.primary}40;

  svg { font-size: 1.2rem; }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 20px ${p => p.theme.colors.primary}50;
  }
`;

/* ─── BOARD ─────────────────────────────────────────────────────── */

const Board = styled.div`
  display: flex;
  gap: 16px;
  overflow-x: auto;
  padding-bottom: ${p => p.theme.spacing.xl};
  align-items: flex-start;
  -webkit-overflow-scrolling: touch;
`;

const Column = styled.div`
  min-width: 280px;
  flex-shrink: 0;
  background: ${p => p.theme.colors.backgroundLight};
  border-radius: ${p => p.theme.borderRadius.lg};
  box-shadow: ${p => p.theme.shadows.card};
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 220px);
`;

const ColumnHeader = styled.div`
  padding: ${p => p.theme.spacing.md} ${p => p.theme.spacing.lg};
  border-bottom: 1px solid ${p => p.theme.colors.border};
  border-left: 4px solid ${p => p.color || p.theme.colors.primary};
  border-radius: ${p => p.theme.borderRadius.lg} ${p => p.theme.borderRadius.lg} 0 0;
`;

const ColumnName = styled.h3`
  font-size: ${p => p.theme.fontSizes.md};
  color: ${p => p.theme.colors.textPrimary};
  margin: 0 0 4px 0;
  font-weight: ${p => p.theme.fontWeights.semibold};
`;

const ColumnMeta = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing.md};
  font-size: ${p => p.theme.fontSizes.xs};
  color: ${p => p.theme.colors.textSecondary};
`;

const ColumnCards = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${p => p.theme.spacing.md};
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.spacing.md};
`;

/* ─── CARD ───────────────────────────────────────────────────────── */

const OppCard = styled.div`
  background: ${p =>
    p.ganada ? p.theme.colors.success + '12' :
    p.perdida ? p.theme.colors.backgroundDark :
    p.theme.colors.background};
  border: 1px solid ${p =>
    p.ganada ? p.theme.colors.success + '40' :
    p.perdida ? p.theme.colors.border :
    p.theme.colors.borderLight};
  border-radius: ${p => p.theme.borderRadius.md};
  padding: ${p => p.theme.spacing.md};
  cursor: pointer;
  opacity: ${p => p.perdida ? 0.6 : 1};
  transition: ${p => p.theme.transitions.fast};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${p => p.theme.shadows.sm};
  }
`;

const CardTitle = styled.div`
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: ${p => p.theme.fontWeights.semibold};
  color: ${p => p.theme.colors.textPrimary};
  margin-bottom: 4px;
`;

const CardMeta = styled.div`
  font-size: ${p => p.theme.fontSizes.xs};
  color: ${p => p.theme.colors.textSecondary};
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 4px;

  svg { font-size: 0.9rem; }
`;

const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${p => p.theme.spacing.sm};
  padding-top: ${p => p.theme.spacing.sm};
  border-top: 1px solid ${p => p.theme.colors.borderLight};
`;

const CardAmount = styled.span`
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: 700;
  color: ${p => p.theme.colors.textPrimary};
`;

const CardProb = styled.span`
  font-size: ${p => p.theme.fontSizes.xs};
  color: ${p => p.theme.colors.textSecondary};
  background: ${p => p.theme.colors.backgroundDark};
  padding: 2px 8px;
  border-radius: 99px;
`;

/* ─── DRAWER ─────────────────────────────────────────────────────── */

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 900;
`;

const Drawer = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: 380px;
  max-width: 95vw;
  height: 100vh;
  background: ${p => p.theme.colors.backgroundLight};
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.15);
  z-index: 901;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const DrawerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${p => p.theme.spacing.lg} ${p => p.theme.spacing.xl};
  border-bottom: 1px solid ${p => p.theme.colors.border};
  flex-shrink: 0;
`;

const DrawerTitle = styled.h2`
  font-size: ${p => p.theme.fontSizes.xl};
  color: ${p => p.theme.colors.textPrimary};
  margin: 0;
`;

const CloseBtn = styled.button`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  color: ${p => p.theme.colors.textSecondary};
  cursor: pointer;
  transition: ${p => p.theme.transitions.fast};

  svg { font-size: 1.25rem; }

  &:hover {
    border-color: ${p => p.theme.colors.primary};
    color: ${p => p.theme.colors.primary};
  }
`;

const DrawerBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${p => p.theme.spacing.xl};
`;

const DrawerFooter = styled.div`
  padding: ${p => p.theme.spacing.lg} ${p => p.theme.spacing.xl};
  border-top: 1px solid ${p => p.theme.colors.border};
  flex-shrink: 0;
`;

/* ─── FORM INSIDE DRAWER ─────────────────────────────────────────── */

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.spacing.sm};
  margin-bottom: ${p => p.theme.spacing.lg};
`;

const Label = styled.label`
  font-size: ${p => p.theme.fontSizes.sm};
  color: ${p => p.theme.colors.textPrimary};
  font-weight: ${p => p.theme.fontWeights.medium};
`;

const Input = styled.input`
  padding: ${p => p.theme.spacing.md};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  background-color: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textPrimary};
  transition: ${p => p.theme.transitions.fast};

  &:focus {
    border-color: ${p => p.theme.colors.primary};
    outline: none;
  }

  &::placeholder { color: ${p => p.theme.colors.textLight}; }
`;

const Select = styled.select`
  padding: ${p => p.theme.spacing.md};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  background-color: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textPrimary};
  cursor: pointer;

  &:focus {
    border-color: ${p => p.theme.colors.primary};
    outline: none;
  }
`;

const TextArea = styled.textarea`
  padding: ${p => p.theme.spacing.md};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  background-color: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textPrimary};
  font-family: ${p => p.theme.fonts.primary};
  min-height: 80px;
  resize: vertical;

  &:focus {
    border-color: ${p => p.theme.colors.primary};
    outline: none;
  }

  &::placeholder { color: ${p => p.theme.colors.textLight}; }
`;

const RadioGroup = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing.lg};
  margin-bottom: ${p => p.theme.spacing.sm};
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
  font-size: ${p => p.theme.fontSizes.sm};
  color: ${p => p.theme.colors.textPrimary};
  cursor: pointer;

  input { accent-color: ${p => p.theme.colors.primary}; }
`;

const BtnRow = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing.md};
`;

const Btn = styled.button`
  flex: 1;
  padding: ${p => p.theme.spacing.md};
  background-color: ${p =>
    p.variant === 'success' ? p.theme.colors.success :
    p.variant === 'danger' ? p.theme.colors.danger :
    p.primary ? p.theme.colors.primary :
    p.theme.colors.backgroundDark};
  color: ${p => (p.primary || p.variant) ? 'white' : p.theme.colors.textPrimary};
  border: none;
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: 600;
  font-family: ${p => p.theme.fonts.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${p => p.theme.spacing.sm};
  cursor: pointer;
  transition: ${p => p.theme.transitions.fast};

  svg { font-size: 1.1rem; }

  &:hover { opacity: 0.9; transform: translateY(-1px); }
  &:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
`;

const DeleteText = styled.button`
  background: none;
  border: none;
  color: ${p => p.theme.colors.danger};
  font-size: ${p => p.theme.fontSizes.xs};
  cursor: pointer;
  padding: 0;
  margin-top: ${p => p.theme.spacing.sm};
  text-decoration: underline;

  &:hover { opacity: 0.7; }
`;

const InfoSection = styled.div`
  background: ${p => p.theme.colors.background};
  border-radius: ${p => p.theme.borderRadius.md};
  padding: ${p => p.theme.spacing.lg};
  margin-bottom: ${p => p.theme.spacing.lg};
  border: 1px solid ${p => p.theme.colors.border};
`;

const InfoRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: ${p => p.theme.spacing.md};

  &:last-child { margin-bottom: 0; }
`;

const InfoLabel = styled.span`
  font-size: ${p => p.theme.fontSizes.xs};
  color: ${p => p.theme.colors.textLight};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 700;
`;

const InfoValue = styled.span`
  font-size: ${p => p.theme.fontSizes.sm};
  color: ${p => p.theme.colors.textPrimary};
  font-weight: ${p => p.theme.fontWeights.medium};
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${p => p.theme.colors.border};
  margin: ${p => p.theme.spacing.lg} 0;
`;

const ErrorMsg = styled.p`
  color: ${p => p.theme.colors.danger};
  font-size: ${p => p.theme.fontSizes.xs};
  margin: 0 0 ${p => p.theme.spacing.md};
`;

/* ─── COMPONENT ─────────────────────────────────────────────────── */

const emptyForm = {
  titulo: '',
  etapa_id: '',
  contacto_tipo: 'cliente',
  cliente_id: '',
  lead_id: '',
  monto_esperado: '',
  probabilidad: '',
  fecha_cierre_esperada: '',
  descripcion: '',
};

const Pipeline = () => {
  const navigate = useNavigate();
  const { confirm } = useModal();
  const [etapas, setEtapas] = useState([]);
  const [oportunidades, setOportunidades] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [leads, setLeads] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [selectedOportunidad, setSelectedOportunidad] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [actionLoading, setActionLoading] = useState(false);
  const [formError, setFormError] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [etapasRes, oppsRes] = await Promise.all([
        api.get('/pipeline/etapas'),
        api.get('/pipeline/oportunidades'),
      ]);
      setEtapas(etapasRes.data.data || etapasRes.data || []);
      setOportunidades(oppsRes.data.data || oppsRes.data || []);
    } catch (err) {
      console.error('Error al cargar pipeline:', err);
    }
  }, []);

  useEffect(() => {
    loadData();
    // Load clientes and leads for dropdowns
    api.get('/clientes').then(r => setClientes(r.data.data || r.data || [])).catch(() => {});
    api.get('/leads').then(r => setLeads(r.data.data || r.data || [])).catch(() => {});
  }, [loadData]);

  const openCreateDrawer = () => {
    setIsCreating(true);
    setSelectedOportunidad(null);
    setForm({ ...emptyForm, etapa_id: etapas[0]?.id || '' });
    setFormError(null);
    setDrawerOpen(true);
  };

  const openDetailDrawer = (opp) => {
    setIsCreating(false);
    setSelectedOportunidad(opp);
    setForm({
      titulo: opp.titulo || '',
      etapa_id: opp.etapa_id || '',
      contacto_tipo: opp.cliente_nombre ? 'cliente' : 'lead',
      cliente_id: '',
      lead_id: '',
      monto_esperado: opp.monto_esperado || '',
      probabilidad: opp.probabilidad || '',
      fecha_cierre_esperada: opp.fecha_cierre_esperada
        ? opp.fecha_cierre_esperada.split('T')[0]
        : '',
      descripcion: opp.descripcion || '',
    });
    setFormError(null);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setIsCreating(false);
    setSelectedOportunidad(null);
    setFormError(null);
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const buildPayload = () => {
    const payload = {
      titulo: form.titulo,
      etapa_id: parseInt(form.etapa_id),
      monto_esperado: parseFloat(form.monto_esperado) || 0,
      probabilidad: parseInt(form.probabilidad) || 0,
      fecha_cierre_esperada: form.fecha_cierre_esperada || null,
      descripcion: form.descripcion || null,
    };
    if (form.contacto_tipo === 'cliente' && form.cliente_id) {
      payload.cliente_id = parseInt(form.cliente_id);
    } else if (form.contacto_tipo === 'lead' && form.lead_id) {
      payload.lead_id = parseInt(form.lead_id);
    }
    return payload;
  };

  const handleCreate = async () => {
    if (!form.titulo || !form.etapa_id) {
      setFormError('El titulo y la etapa son requeridos');
      return;
    }
    setActionLoading(true);
    setFormError(null);
    try {
      await api.post('/pipeline/oportunidades', buildPayload());
      await loadData();
      closeDrawer();
    } catch (err) {
      setFormError(err.message || 'Error al crear oportunidad');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdate = async () => {
    setActionLoading(true);
    setFormError(null);
    try {
      await api.put(`/pipeline/oportunidades/${selectedOportunidad.id}`, buildPayload());
      await loadData();
      closeDrawer();
    } catch (err) {
      setFormError(err.message || 'Error al actualizar oportunidad');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEstado = async (estado) => {
    setActionLoading(true);
    try {
      await api.put(`/pipeline/oportunidades/${selectedOportunidad.id}`, { estado });
      await loadData();
      closeDrawer();
    } catch (err) {
      setFormError(err.message || 'Error al cambiar estado');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    const ok = await confirm('¿Eliminar esta oportunidad? Esta acción no se puede deshacer.', {
      title: 'Eliminar oportunidad',
      confirmText: 'Eliminar',
      danger: true,
    });
    if (!ok) return;
    setActionLoading(true);
    try {
      await api.delete(`/pipeline/oportunidades/${selectedOportunidad.id}`);
      await loadData();
      closeDrawer();
    } catch (err) {
      setFormError(err.message || 'Error al eliminar');
    } finally {
      setActionLoading(false);
    }
  };

  // Pipeline value = SUM(monto_esperado * probabilidad / 100) for active opps
  const pipelineValue = oportunidades
    .filter(o => o.estado === 'activa' || (!o.estado || (o.estado !== 'ganada' && o.estado !== 'perdida')))
    .reduce((sum, o) => sum + (parseFloat(o.monto_esperado) || 0) * (parseFloat(o.probabilidad) || 0) / 100, 0);

  const getOppsForEtapa = (etapaId) =>
    oportunidades.filter(o => o.etapa_id === etapaId);

  const getEtapaTotal = (etapaId) =>
    getOppsForEtapa(etapaId).reduce((sum, o) => sum + (parseFloat(o.monto_esperado) || 0), 0);

  return (
    <PageContainer>
      <PageHeader>
        <HeaderLeft>
          <Title>Pipeline de Ventas</Title>
          <PipelineValue>
            <MdTrendingUp />
            Valor ponderado:&nbsp;
            <strong>
              ${pipelineValue.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
            </strong>
          </PipelineValue>
        </HeaderLeft>
        <AddButton onClick={openCreateDrawer}>
          <MdAdd />
          Nueva Oportunidad
        </AddButton>
      </PageHeader>

      <Board>
        {etapas.map(etapa => {
          const opps = getOppsForEtapa(etapa.id);
          const totalEtapa = getEtapaTotal(etapa.id);
          return (
            <Column key={etapa.id}>
              <ColumnHeader color={etapa.color}>
                <ColumnName>{etapa.nombre}</ColumnName>
                <ColumnMeta>
                  <span>{opps.length} oportunidad{opps.length !== 1 ? 'es' : ''}</span>
                  <span>·</span>
                  <span>${totalEtapa.toLocaleString('es-MX', { minimumFractionDigits: 0 })}</span>
                </ColumnMeta>
              </ColumnHeader>
              <ColumnCards>
                {opps.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '24px 0', color: '#9ca3af', fontSize: '0.8rem' }}>
                    Sin oportunidades
                  </div>
                )}
                {opps.map(opp => {
                  const ganada = opp.estado === 'ganada';
                  const perdida = opp.estado === 'perdida';
                  return (
                    <OppCard
                      key={opp.id}
                      ganada={ganada}
                      perdida={perdida}
                      onClick={() => openDetailDrawer(opp)}
                    >
                      <CardTitle>{opp.titulo}</CardTitle>
                      {(opp.cliente_nombre || opp.lead_nombre) && (
                        <CardMeta>
                          <MdPerson />
                          {opp.cliente_nombre || opp.lead_nombre}
                        </CardMeta>
                      )}
                      {opp.fecha_cierre_esperada && (
                        <CardMeta>
                          <MdCalendarToday />
                          {new Date(opp.fecha_cierre_esperada).toLocaleDateString('es-MX')}
                        </CardMeta>
                      )}
                      <CardFooter>
                        <CardAmount>
                          ${(parseFloat(opp.monto_esperado) || 0).toLocaleString('es-MX', { minimumFractionDigits: 0 })}
                        </CardAmount>
                        {opp.probabilidad != null && (
                          <CardProb>{opp.probabilidad}%</CardProb>
                        )}
                      </CardFooter>
                    </OppCard>
                  );
                })}
              </ColumnCards>
            </Column>
          );
        })}
        {etapas.length === 0 && (
          <div style={{ padding: '40px', color: '#6b7280' }}>
            Cargando pipeline...
          </div>
        )}
      </Board>

      {/* ─── DRAWER ─── */}
      {drawerOpen && (
        <>
          <Overlay onClick={closeDrawer} />
          <Drawer>
            <DrawerHeader>
              <DrawerTitle>
                {isCreating ? 'Nueva Oportunidad' : selectedOportunidad?.titulo}
              </DrawerTitle>
              <CloseBtn onClick={closeDrawer}>
                <MdClose />
              </CloseBtn>
            </DrawerHeader>

            <DrawerBody>
              {/* ── DETAIL MODE ── */}
              {!isCreating && selectedOportunidad && (
                <>
                  <InfoSection>
                    <InfoRow>
                      <InfoLabel>Estado</InfoLabel>
                      <InfoValue style={{ textTransform: 'capitalize' }}>
                        {selectedOportunidad.estado || 'activa'}
                      </InfoValue>
                    </InfoRow>
                    <InfoRow>
                      <InfoLabel>Etapa</InfoLabel>
                      <InfoValue>{selectedOportunidad.etapa_nombre || 'N/A'}</InfoValue>
                    </InfoRow>
                    {(selectedOportunidad.cliente_nombre || selectedOportunidad.lead_nombre) && (
                      <InfoRow>
                        <InfoLabel>Contacto</InfoLabel>
                        <InfoValue>
                          {selectedOportunidad.cliente_nombre || selectedOportunidad.lead_nombre}
                        </InfoValue>
                      </InfoRow>
                    )}
                    <InfoRow>
                      <InfoLabel>Monto Esperado</InfoLabel>
                      <InfoValue>
                        ${(parseFloat(selectedOportunidad.monto_esperado) || 0)
                          .toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                      </InfoValue>
                    </InfoRow>
                    {selectedOportunidad.probabilidad != null && (
                      <InfoRow>
                        <InfoLabel>Probabilidad</InfoLabel>
                        <InfoValue>{selectedOportunidad.probabilidad}%</InfoValue>
                      </InfoRow>
                    )}
                    {selectedOportunidad.fecha_cierre_esperada && (
                      <InfoRow>
                        <InfoLabel>Cierre Esperado</InfoLabel>
                        <InfoValue>
                          {new Date(selectedOportunidad.fecha_cierre_esperada).toLocaleDateString('es-MX')}
                        </InfoValue>
                      </InfoRow>
                    )}
                  </InfoSection>

                  <Divider />
                  <Label style={{ display: 'block', marginBottom: '12px', fontWeight: 600 }}>
                    Editar Oportunidad
                  </Label>
                </>
              )}

              {/* ── SHARED FORM FIELDS ── */}
              {isCreating && (
                <FormGroup>
                  <Label>Titulo <span style={{ color: 'red' }}>*</span></Label>
                  <Input
                    type="text"
                    name="titulo"
                    value={form.titulo}
                    onChange={handleChange}
                    placeholder="Nombre de la oportunidad"
                  />
                </FormGroup>
              )}

              <FormGroup>
                <Label>Etapa <span style={{ color: 'red' }}>*</span></Label>
                <Select name="etapa_id" value={form.etapa_id} onChange={handleChange}>
                  <option value="">Seleccionar etapa...</option>
                  {etapas.map(e => (
                    <option key={e.id} value={e.id}>{e.nombre}</option>
                  ))}
                </Select>
              </FormGroup>

              {isCreating && (
                <FormGroup>
                  <Label>Tipo de Contacto</Label>
                  <RadioGroup>
                    <RadioLabel>
                      <input
                        type="radio"
                        name="contacto_tipo"
                        value="cliente"
                        checked={form.contacto_tipo === 'cliente'}
                        onChange={handleChange}
                      />
                      Cliente
                    </RadioLabel>
                    <RadioLabel>
                      <input
                        type="radio"
                        name="contacto_tipo"
                        value="lead"
                        checked={form.contacto_tipo === 'lead'}
                        onChange={handleChange}
                      />
                      Lead
                    </RadioLabel>
                  </RadioGroup>
                  {form.contacto_tipo === 'cliente' ? (
                    <Select name="cliente_id" value={form.cliente_id} onChange={handleChange}>
                      <option value="">Sin cliente asignado</option>
                      {clientes.map(c => (
                        <option key={c.id} value={c.id}>{c.nombre}</option>
                      ))}
                    </Select>
                  ) : (
                    <Select name="lead_id" value={form.lead_id} onChange={handleChange}>
                      <option value="">Sin lead asignado</option>
                      {leads.map(l => (
                        <option key={l.id} value={l.id}>{l.nombre}</option>
                      ))}
                    </Select>
                  )}
                </FormGroup>
              )}

              <FormGroup>
                <Label>Monto Esperado ($)</Label>
                <Input
                  type="number"
                  name="monto_esperado"
                  value={form.monto_esperado}
                  onChange={handleChange}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                />
              </FormGroup>

              <FormGroup>
                <Label>Probabilidad (%)</Label>
                <Input
                  type="number"
                  name="probabilidad"
                  value={form.probabilidad}
                  onChange={handleChange}
                  placeholder="0-100"
                  min="0"
                  max="100"
                />
              </FormGroup>

              <FormGroup>
                <Label>Fecha de Cierre Esperada</Label>
                <Input
                  type="date"
                  name="fecha_cierre_esperada"
                  value={form.fecha_cierre_esperada}
                  onChange={handleChange}
                />
              </FormGroup>

              <FormGroup>
                <Label>Descripcion</Label>
                <TextArea
                  name="descripcion"
                  value={form.descripcion}
                  onChange={handleChange}
                  placeholder="Detalles de la oportunidad..."
                />
              </FormGroup>

              {formError && <ErrorMsg>{formError}</ErrorMsg>}

              {/* ── GANAR / PERDER buttons (detail only) ── */}
              {!isCreating && selectedOportunidad && (
                <>
                  <Divider />
                  <Label style={{ display: 'block', marginBottom: '12px', fontWeight: 600 }}>
                    Cambiar Estado
                  </Label>
                  <BtnRow>
                    <Btn
                      variant="success"
                      onClick={() => handleEstado('ganada')}
                      disabled={actionLoading || selectedOportunidad.estado === 'ganada'}
                    >
                      <MdCheck />
                      Ganar
                    </Btn>
                    <Btn
                      variant="danger"
                      onClick={() => handleEstado('perdida')}
                      disabled={actionLoading || selectedOportunidad.estado === 'perdida'}
                    >
                      <MdCancel />
                      Perder
                    </Btn>
                  </BtnRow>
                </>
              )}
            </DrawerBody>

            <DrawerFooter>
              {isCreating ? (
                <Btn primary onClick={handleCreate} disabled={actionLoading}>
                  <MdAdd />
                  {actionLoading ? 'Creando...' : 'Crear Oportunidad'}
                </Btn>
              ) : (
                <>
                  <Btn primary onClick={handleUpdate} disabled={actionLoading} style={{ width: '100%', marginBottom: '8px' }}>
                    <MdCheck />
                    {actionLoading ? 'Guardando...' : 'Actualizar'}
                  </Btn>
                  <div style={{ textAlign: 'center' }}>
                    <DeleteText onClick={handleDelete} disabled={actionLoading}>
                      Eliminar oportunidad
                    </DeleteText>
                  </div>
                </>
              )}
            </DrawerFooter>
          </Drawer>
        </>
      )}
    </PageContainer>
  );
};

export default Pipeline;
